import LoadingButton from '@mui/lab/LoadingButton';
import {Button, DialogActions, DialogContent, MenuItem, Stack, TextField} from '@mui/material';
import {Survey} from 'crqlar-react-common/dist/models/survey/survey.model';
import {SurveyService} from 'crqlar-react-common/dist/services/survey/survey.service';
import {getNameForLanguage} from 'crqlar-react-common/dist/utils/translations.util';
import {enqueueSnackbar} from 'notistack';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {getSendGridSnackbarProps} from '../../../utils/reservationNotification.util';
import SurveyInviteHeader from './SurveyInviteHeader';
import SurveyInviteSurveyField from './SurveyInviteSurveyField';
import {INVITE_EMAIL_PATTERN} from './surveyShare.constants';

interface Props {
    /** From the survey's own page: preselected, drafts included. */
    survey?: Survey
    onBack: () => void
    onClose: () => void
}

/**
 * Send test: the survey as a guest gets it, mailed to any address — by default the signed-in user's own. Drafts can be
 * tested; the link always opens the latest saved state, and nothing is saved when it's answered.
 */
export default function SurveyInviteTestStep({survey: knownSurvey, onBack, onClose}: Props) {
    const {t, i18n} = useTranslation();
    const ownEmail = useSelector<any, string | undefined>(state => state.auth.user?.email);
    const [email, setEmail] = useState(ownEmail ?? '');
    const [emailInvalid, setEmailInvalid] = useState(false);
    const [survey, setSurvey] = useState<Survey | undefined>(knownSurvey);
    const [chosenLanguage, setChosenLanguage] = useState<string>();
    const [sending, setSending] = useState(false);

    const languages = survey?.languages?.filter(Boolean) ?? [];
    // The staff member's own language when the survey offers it, else the survey's first.
    const language = [chosenLanguage, i18n.language?.slice(0, 2)].find(candidate => !!candidate && languages.includes(candidate))
        ?? languages[0];

    const send = async () => {
        const address = email.trim();
        if (!INVITE_EMAIL_PATTERN.test(address)) {
            setEmailInvalid(true);
            return;
        }
        if (!survey?.id || sending) return;
        setSending(true);
        try {
            const result = await SurveyService.invitationSendTest({surveyId: survey.id, email: address, language});
            const feedback = getSendGridSnackbarProps(t, result.statusCode);
            enqueueSnackbar(feedback.message, {variant: feedback.variant});
            if (result.statusCode >= 200 && result.statusCode < 300) onBack();
        } catch {
            enqueueSnackbar(t('The test could not be sent.'), {variant: 'error'});
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <SurveyInviteHeader
                title={t('Send test')}
                subtitle={t('Answers to a test are not saved.')}
                onBack={onBack}
                onClose={onClose}
            />
            <DialogContent sx={{px: 2.5, '&&': {pt: 1}}}>
                <Stack gap={2}>
                    <TextField
                        autoFocus
                        fullWidth
                        size={'small'}
                        type={'email'}
                        label={t('Email')}
                        value={email}
                        error={emailInvalid}
                        helperText={emailInvalid ? t('Enter a valid email address') : undefined}
                        onChange={event => {
                            setEmail(event.target.value);
                            setEmailInvalid(false);
                        }}
                    />
                    <SurveyInviteSurveyField value={survey} onChange={setSurvey} forTest/>
                    {languages.length > 1 && (
                        <TextField
                            select
                            fullWidth
                            size={'small'}
                            label={t('Language')}
                            value={language ?? ''}
                            onChange={event => setChosenLanguage(event.target.value)}
                        >
                            {languages.map(code => (
                                <MenuItem key={code} value={code}>{getNameForLanguage(code, true) || code}</MenuItem>
                            ))}
                        </TextField>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 2.5, pb: 2.5}}>
                <Button onClick={onBack} disabled={sending}>{t('Cancel')}</Button>
                <LoadingButton variant={'contained'} disableElevation loading={sending} disabled={!survey?.id} onClick={send}>
                    {t('Send test')}
                </LoadingButton>
            </DialogActions>
        </>
    );
}
